import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Solve the largest font size at which a ONE-LINE figure still fits its box.
 *
 * ── Why this exists (Finance book Ui #380) ──────────────────────────────────
 * A money figure must never break across lines: "+US$124,36 / 2,509.47" is not
 * a contained number, it is a misread one. So the text is `whitespace-nowrap`
 * and the SIZE is what gives — but a fixed size (or a binary breakpoint step)
 * cannot know how wide the string actually is, which is exactly how #380 failed
 * twice. First the figure broke mid-value; then, once `nowrap` stopped it
 * breaking, it simply bled out of its card instead.
 *
 * The solve is exact rather than iterative: rendered text width scales LINEARLY
 * with font-size (the panel's `tracking` is expressed in `em`, so it scales
 * too), so measuring the string once at the size it is currently drawn at gives
 * the size at which it exactly fills the available width:
 *
 *     ideal = currentFontPx × available / naturalWidth
 *
 * ── Usage ───────────────────────────────────────────────────────────────────
 *     const net = useFittedFigure({ maxPx: 44, minPx: 16, deps: [totals] });
 *     <div ref={net.containerRef}>                       // the width budget
 *       <span ref={net.figureRef} style={{ fontSize: net.fontPx }}
 *             className="whitespace-nowrap">{amount}</span>
 *     </div>
 *
 * `containerRef` goes on the element whose `clientWidth` IS the budget — for a
 * figure inside a wrapping flex header that is the whole header row, because a
 * total too wide to sit beside its title drops to its own full-width line and
 * that line is what it must fit.
 *
 * ── Two implementation details that are load-bearing ────────────────────────
 * 1. CALLBACK REFS, not `useRef` + a mount-time effect. These figures live in
 *    conditionally-rendered subtrees (a split card exists only when that
 *    direction has rows), so an effect with `[]` deps would find `null` on
 *    mount and never look again — the documented #142 / BoardView trap.
 * 2. The solve is KEYED on `(text, available)`. It reads `scrollWidth`, an
 *    integer, so re-solving from its own result could oscillate by a pixel
 *    forever; keying on the inputs makes each state settle after one solve.
 */
export default function useFittedFigure({ maxPx, minPx, deps = [] }) {
    const [containerEl, setContainerEl] = useState(null);
    const [figureEl, setFigureEl] = useState(null);
    const [fontPx, setFontPx] = useState(maxPx);
    // Bumped by the ResizeObserver. The solver re-reads the live DOM, so this
    // only has to be a change SIGNAL — it never needs to carry the width.
    const [resizeTick, setResizeTick] = useState(0);
    const fitKeyRef = useRef('');

    const containerRef = useCallback((node) => setContainerEl(node), []);
    const figureRef = useCallback((node) => setFigureEl(node), []);

    useLayoutEffect(() => {
        if (!containerEl || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setResizeTick((n) => n + 1));
        ro.observe(containerEl);
        return () => ro.disconnect();
    }, [containerEl]);

    useLayoutEffect(() => {
        if (!containerEl || !figureEl) return;
        const available = containerEl.clientWidth;
        // jsdom (unit tests) reports 0 for every layout metric — leave the
        // figure at its declared maximum rather than "solving" against zeros.
        if (!available) return;
        const key = `${figureEl.textContent}|${available}`;
        if (fitKeyRef.current === key) return;
        const current = parseFloat(window.getComputedStyle(figureEl).fontSize) || maxPx;
        // `whitespace-nowrap` makes scrollWidth the natural ONE-LINE width at
        // the size currently rendered.
        const natural = figureEl.scrollWidth;
        if (!natural) return;
        const ideal = (current * available) / natural;
        const next = Math.max(minPx, Math.min(maxPx, Math.floor(ideal)));
        fitKeyRef.current = key;
        setFontPx((prev) => (prev === next ? prev : next));
        // `fontPx` is intentionally in the dep list: after a resize the solve
        // runs, sets a new size, and must re-run once at that size to confirm
        // the key — which is what the key guard then short-circuits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerEl, figureEl, resizeTick, fontPx, maxPx, minPx, ...deps]);

    return { containerRef, figureRef, fontPx };
}
